import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import BottomTabBar from "../components/BottomTabBar";

const spiceColor = (s) =>
  s === "spicy" ? "#7B61FF" : s === "flirty" ? "#FF4D8D" : "#00E5FF";

export default function History() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    api
      .getHistory()
      .then(setItems)
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="warm-bg min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF4D8D]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="warm-bg min-h-screen">
        <Header />
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <Sparkles className="w-10 h-10 text-[#00E5FF] mx-auto mb-5" fill="#00E5FF" />
          <h1 className="font-display text-3xl font-bold text-white mb-3">
            Sign in to see your <span className="gradient-text">history</span>
          </h1>
          <p className="text-[#C9C7D9] mb-7">
            Save and revisit all your spicy moments together.
          </p>
          <Link
            to="/login"
            className="inline-block btn-gradient px-8 py-3.5 rounded-full font-bold"
            data-testid="history-login-link"
          >
            Sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="warm-bg min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#C9C7D9] hover:text-white font-semibold mb-6" data-testid="history-back-link">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">
          Your <span className="gradient-text">history</span>
        </h1>
        <p className="text-[#C9C7D9] mb-10">All the rounds you've played.</p>

        {items.length === 0 ? (
          <div className="card-glow rounded-3xl p-12 text-center">
            <p className="text-[#C9C7D9] font-semibold">No rounds yet. Play a game and come back!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((it, idx) => {
              const color = spiceColor(it.spice_level);
              return (
                <motion.div
                  key={it.id || idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(idx * 0.04, 0.6) }}
                  className="card-glow rounded-2xl p-6"
                  data-testid={`history-item-${idx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-xs font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full"
                      style={{
                        background: `${color}15`,
                        color,
                        border: `1px solid ${color}40`,
                      }}
                    >
                      {it.mode?.replaceAll("_", " ")} · {it.spice_level}
                    </span>
                    <span className="text-xs text-[#C9C7D9]">
                      {new Date(it.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="font-semibold text-white">
                    {it.content?.truth || it.content?.dare || it.content?.question || it.content?.title || "—"}
                  </p>
                  {it.action && (
                    <span className="text-xs text-[#C9C7D9] mt-2 inline-block">
                      → {it.action}
                    </span>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
        <div className="h-44" aria-hidden />
      </main>
      <BottomTabBar active="rooms" />
    </div>
  );
}
